/**
 * VRG-726 — what `<Routes location>` is given, open or closed. NEVER `undefined`.
 *
 * ## The bug this exists to prevent
 *
 * Opening the support intake reset the page behind it: an unsaved edit on Settings → General
 * was gone when the modal opened, from the Help popover's "Report a bug" and from an error
 * toast's "Report a problem" alike. The page was REMOUNTED, not re-rendered.
 *
 * The cause is in react-router itself (6.30, `useRoutesImpl`): when `<Routes>` is given a
 * `location` prop it wraps its matches in an extra `<LocationContext.Provider>`; without one it
 * returns the matches bare. The provider used to pass `undefined` while the intake was closed
 * and the background location while it was open, so opening the modal changed the element TYPE
 * at the root of everything under `<Routes>` (Layout, the sidebar, the page), and React unmounts
 * a subtree whose root type changes. Every piece of local state below it, a half-typed form
 * included, was thrown away; closing the modal did it again.
 *
 * Always passing a location keeps the tree's shape constant. While closed it is the REAL current
 * location, so routing is unchanged; while open it is the page the user was on, so that page
 * keeps rendering under the modal with its state intact.
 *
 * The one behavioural difference, checked: under an explicit location react-router reports
 * `useNavigationType()` as POP inside `<Routes>`. Nothing in the platform reads it.
 *
 * ZERO runtime imports (the one import is type-only), so it is unit-tested under node
 * (`test/support-queue-source.guard.spec.ts`).
 */
import type { Location } from 'react-router-dom'

/** Where a cold-loaded intake shows its backdrop: a route that renders a page, never a redirect. */
export type IntakeFallback = string

export const routesLocationFor = ({
  isOpen,
  background,
  current,
  fallback,
}: {
  /** The URL is the intake route. */
  isOpen: boolean
  /** The last location that was not the intake, or null on a cold load. */
  background: Location | null
  /** The real current location (`useLocation()` outside `<Routes>`). */
  current: Location
  fallback: IntakeFallback
}): Location | IntakeFallback => (isOpen ? background ?? fallback : current)
